/**
 * Location adapter abstraction for browser location operations.
 * Enables testability by allowing location access to be controlled in tests.
 */

/**
 * Interface for location operations.
 */
export interface LocationAdapter {
    /**
     * Set the window location href (triggers navigation).
     */
    setHref(url: string): void

    /**
     * Get the current pathname.
     */
    getPathname(): string

    /**
     * Get the current search string (including leading ?).
     */
    getSearch(): string
}

/**
 * Browser location adapter using window.location.
 * Use this in production code.
 */
export const browserLocation: LocationAdapter = {
    setHref: (url) => {
        window.location.href = url
    },
    getPathname: () => window.location.pathname,
    getSearch: () => window.location.search,
}

/**
 * Factory function to create a test location adapter.
 * Use this in tests to avoid actual navigation and inspect redirect URLs.
 *
 * @param initialPathname - The initial pathname (defaults to '/')
 * @param initialSearch - The initial search string (defaults to '')
 * @returns A LocationAdapter with additional methods for inspection
 */
export const createTestLocation = (
    initialPathname = '/',
    initialSearch = ''
): LocationAdapter & {
    /**
     * Get the last URL that was set via setHref.
     */
    getLastHref: () => string | null
    /**
     * Get all URLs that were set via setHref.
     */
    getAllHrefs: () => string[]
    /**
     * Reset the recorded href history.
     */
    reset: () => void
} => {
    let lastHref: string | null = null
    const allHrefs: string[] = []
    let pathname = initialPathname
    let search = initialSearch

    return {
        setHref: (url) => {
            lastHref = url
            allHrefs.push(url)
        },
        getPathname: () => pathname,
        getSearch: () => search,
        getLastHref: () => lastHref,
        getAllHrefs: () => [...allHrefs],
        reset: () => {
            lastHref = null
            allHrefs.length = 0
            pathname = initialPathname
            search = initialSearch
        },
    }
}
